import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import './PaginationComponent.scss';

export interface PaginationComponentProps {
    totalResults: number;
    limit: number;
    page: number;
    paginationOptions?: number[];
    onPageChange: (page: number) => void;
    onRowsPerPageChange: (limit: number) => void;
    id?: string;
}

const PaginationComponent: React.FC<PaginationComponentProps> = ({
    totalResults,
    limit,
    page,
    paginationOptions = [10, 20, 50, 100],
    onPageChange,
    onRowsPerPageChange,
    id
}) => {
    const totalPages = Math.ceil(totalResults / limit);
    const startItem = (page - 1) * limit + 1;
    const endItem = Math.min(page * limit, totalResults);

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= totalPages) {
            onPageChange(newPage);
        }
    };

    const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newLimit = parseInt(event.target.value);
        onRowsPerPageChange(newLimit);
    };

    const renderPageNumbers = () => {
        const pages = [];
        const maxVisiblePages = 5;
        
        if (totalPages <= maxVisiblePages) {
            // Show all pages if total is small
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            // Show pages around current page
            let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
            const endPage = Math.min(page + 2, totalPages);
            
            if (endPage - startPage + 1 < maxVisiblePages) {
                startPage = Math.max(1, endPage - maxVisiblePages + 1);
            }
            
            for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
            }
        }
        
        return pages;
    };

    if (totalResults === 0) {
        return null;
    }

    return (
        <div className="pagination-component" id={id}>
            <div className="pagination-info">
                <span>
                    Showing {startItem} to {endItem} of {totalResults} results
                </span>
            </div>
            
            <div className="pagination-controls">
                <div className="rows-per-page">
                    <label htmlFor="rows-per-page">Rows per page:</label>
                    <select
                        id="rows-per-page"
                        value={limit}
                        onChange={handleRowsPerPageChange}
                        className="rows-per-page-select"
                    >
                        {paginationOptions.map(option => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                </div>
                
                <div className="page-navigation">
                    <Button
                        onClick={() => handlePageChange(page - 1)}
                        disabled={page <= 1}
                        className="pagination-button"
                    >
                        <ChevronLeft className="h-4 w-4" />
                        Previous
                    </Button>
                    
                    <div className="page-numbers">
                        {renderPageNumbers().map(pageNum => (
                            <Button
                                key={pageNum}
                                onClick={() => handlePageChange(pageNum)}
                                className={`page-number ${pageNum === page ? 'active' : ''}`}
                            >
                                {pageNum}
                            </Button>
                        ))}
                    </div>
                    
                    <Button
                        onClick={() => handlePageChange(page + 1)}
                        disabled={page >= totalPages}
                        className="pagination-button"
                    >
                        Next
                        <ChevronRight className="h-4 w-4" />
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default PaginationComponent; 